/**
 * Mount once in the shell. Renders nothing. While this browser has the automatic
 * send turned ON, every 15 minutes: read the WIPs with the live scans over them,
 * run the CHECK, and send only the gap it names — exactly what a person pressing
 * Check then Send does, with the same safety (insert-only per WIP, blanks only).
 */
import { useEffect } from "react";
import { buildErpSheets, defaultSelection } from "./fulfillmentExport";
import { loadHandoverWips } from "./handoverLoad";
import {
  ErpConsentRequiredError, gapSheets, sendHandover, sendableSheets,
} from "./erpHandoverClient";
import {
  handoverDue, handoverExplain, handoverNextDelay,
} from "./handoverAutoPlan";
import {
  getHandoverAutoState, readHandoverLastRun, recordHandoverAutoResult,
  setHandoverAutoRunning, useHandoverAutoState, writeHandoverLastRun,
} from "./handoverAutoStore";

export async function runHandoverAuto(): Promise<void> {
  setHandoverAutoRunning(true);
  const at = Date.now();
  try {
    const { wips, missing } = await loadHandoverWips();
    if (missing.length > 0) {
      // A scan list that could not be read would send a handover with that station
      // missing. Never quietly: skip and say so.
      recordHandoverAutoResult({ at, status: "skipped", message: `could not read ${missing.join(", ")}; nothing sent.` });
      return;
    }
    const sheets = buildErpSheets(wips);
    const picked = defaultSelection(sheets);
    const direct = sendableSheets(sheets.filter((s) => picked.has(s.fileName)));
    const checked = await sendHandover(direct, true);
    const gap = gapSheets(direct, checked);
    const rows = gap.reduce((n, s) => n + s.rows.length, 0);
    if (rows === 0) {
      recordHandoverAutoResult({ at, status: "ok", message: "their ERP already has everything — nothing to send." });
      return;
    }
    const sent = await sendHandover(gap, false);
    if (!sent.wrote) {
      recordHandoverAutoResult({ at, status: "skipped", message: "their ERP refused the write (handover is not enabled for this account); nothing was changed." });
      return;
    }
    recordHandoverAutoResult({
      at, status: "ok",
      message: `sent ${sent.totals.inserted} new row(s), filled ${sent.totals.filled} blank cell(s).`,
    });
  } catch (e) {
    recordHandoverAutoResult({
      at, status: e instanceof ErpConsentRequiredError ? "skipped" : "failed",
      message: handoverExplain(e),
    });
  } finally {
    setHandoverAutoRunning(false);
  }
}

export function HandoverAutoRunner() {
  const { enabled } = useHandoverAutoState();
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let timer: number | undefined;
    const schedule = (): void => {
      timer = window.setTimeout(() => { void tick(); },
        handoverNextDelay(Date.now(), readHandoverLastRun()));
    };
    const tick = async (): Promise<void> => {
      if (cancelled) return;
      if (handoverDue(Date.now(), readHandoverLastRun()) && navigator.onLine && !getHandoverAutoState().running) {
        // Stamped BEFORE the run so a sibling tab stands down, and a failing run
        // is retried in 15 minutes, not in a loop.
        writeHandoverLastRun(Date.now());
        await runHandoverAuto();
      }
      if (!cancelled) schedule();
    };
    schedule();
    return () => { cancelled = true; if (timer !== undefined) window.clearTimeout(timer); };
  }, [enabled]);
  return null;
}
